import { T } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SwissCross } from "@/components/Header";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "super_admin" ? "/admin" : "/");
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`${T("Bentornato", "Welcome back")}${u.name ? " " + u.name : ""}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-[#09090b] text-white relative overflow-hidden">
      <MatrixCanvas />
      <div className="relative flex-1 flex items-center justify-center p-6 z-10">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <img src="/brand/aurora-mark.webp" alt="AURORA-ADV logo · agenzia marketing digitale Ticino" className="w-10 h-10" />
            <div>
              <div className="font-display text-lg leading-none">Aurora<span className="aurora-text">-ADV</span></div>
              <div className="text-[10px] uppercase tracking-widest text-[#71717a] font-mono mt-1 flex items-center gap-1.5">
                <SwissCross className="w-2.5 h-2.5" /> Made in Switzerland
              </div>
            </div>
          </Link>

          <h1 className="font-display text-4xl mb-2">{T("Accedi")}</h1>
          <p className="text-sm text-[#a1a1aa] mb-8">{T("Entra nella piattaforma leader per lead targeting AI.")}</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717a]" />
              <Input
                data-testid={TID.emailInput}
                type="text"
                autoComplete="email"
                placeholder={T("email@azienda.com")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] rounded-sm font-mono text-sm"
                required
              />
            </div>
            <PasswordInput testid={TID.passwordInput} autoComplete="current-password" placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)} />
            <div className="text-right -mt-1">
              <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs text-[#a1a1aa] hover:text-white underline-offset-2 hover:underline">{T("Password dimenticata?", "Forgot password?")}</Link>
            </div>
            <Button
              type="submit"
              data-testid={TID.submitLoginBtn}
              disabled={loading}
              className="w-full h-12 bg-white hover:bg-[#E0262C] text-black hover:text-white rounded-sm font-medium transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : T("Accedi")}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-[10px] text-[#52525b] uppercase tracking-widest font-mono">
            <div className="flex-1 h-px bg-[#27272a]" />
            {T("oppure")}
            <div className="flex-1 h-px bg-[#27272a]" />
          </div>

          <Button
            data-testid={TID.googleLoginBtn}
            onClick={onGoogle}
            variant="outline"
            className="w-full h-12 bg-transparent border-[#27272a] text-white hover:bg-[#18181b] hover:text-white rounded-sm"
          >
            <GoogleIcon className="w-4 h-4 mr-2" />
            {T("Continua con Google")}
          </Button>

          <p className="mt-8 text-sm text-[#71717a] text-center">
            {T("Non hai un account?")}{" "}
            <Link data-testid={TID.goToRegister} to="/register" className="aurora-text font-medium">{T("Registrati")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const MatrixCanvas = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0" style={{
      backgroundImage: "radial-gradient(ellipse at top left, rgba(224,38,44,0.14), transparent 50%), radial-gradient(ellipse at bottom right, rgba(43,48,54,0.5), transparent 50%)",
    }}/>
    <pre className="absolute top-0 right-0 text-[10px] leading-none text-[#10b981] font-mono opacity-20 select-none p-4" style={{ textShadow: "0 0 8px rgba(16,185,129,0.5)" }}>
{`01010100 01110010
01100001 01100011
01101011 01010011
01100011 01100001
01101110 00100000
41 55 52 4F 52 41
2D 41 44 56 20
53 57 49 53 53 
52 4F 52 41 2D
41 44 56 20 53
57 49 53 53 20`}
    </pre>
  </div>
);
